import { Suspense, lazy, useEffect } from "react";
import { PERF_ON } from "./lib/perfFlag";
import { UiAtlas } from "./ui/UiIcon";
import { Routes, Route, useLocation } from "react-router-dom";
import { ROUTES } from "./lib/routes";
import { Landing } from "./routes/Landing";
import { ExternalLinkDialog } from "./ui/ExternalLinkDialog";
import { ChickLoader } from "./ui/Loaders";

// 코드 스플릿(FR-COM-09 / S3-1): 무거운 엔진 라우트는 동적 import.
// 지금은 자리표시지만, 향후 Phaser·A-Frame 번들이 첫 진입(랜딩)에 섞이지 않도록
// 처음부터 lazy 경계를 잡아둔다.
import { ClassModeBadge } from "./ui/ClassModeBadge";
import { OfflineBanner } from "./ui/OfflineBanner";
import { NightShade, RestGuard } from "./ui/RestGuard";
import { applyPalette } from "./lib/theme";
import { setClassCode } from "./lib/classMode";
import { classCodeFromSearch } from "@greg-chick/shared";

const Book = lazy(() => import("./routes/Book"));
const Ar = lazy(() => import("./routes/Ar"));
const Verse = lazy(() => import("./routes/Verse"));
const World = lazy(() => import("./routes/World"));
const HeroRestore = lazy(() => import("./routes/HeroRestore"));
const Rally = lazy(() => import("./routes/Rally"));
const Community = lazy(() => import("./routes/Community"));
const Guide = lazy(() => import("./routes/Guide"));
/** 👪 보호자 화면 — 어른 확인 뒤 굿즈·행사·소식(영유아 모드, 조치계획서 ■1) */
const Family = lazy(() => import("./routes/Family"));
const FamilyVoice = lazy(() => import("./routes/FamilyVoice"));
/** 🔒 개인정보처리방침 — 기관·학교가 가리킬 수 있는 주소 하나(조치계획서 ■10) */
const Privacy = lazy(() => import("./routes/Privacy"));
/** 📜 오픈소스·자료 고지 — 목록은 빌드가 package.json 에서 읽는다(조치계획서 ■7) */
const Licenses = lazy(() => import("./routes/Licenses"));
const FamilyBook = lazy(() => import("./routes/FamilyBook"));

const Goods = lazy(() => import("./routes/Goods"));
const GoodsList = lazy(() => import("./routes/GoodsList"));
const GoodsPreview = lazy(() => import("./routes/GoodsPreview"));
const NotFound = lazy(() => import("./routes/NotFound"));

function RouteFallback() {
  return <ChickLoader />;
}

/**
 * 🏫 **주소에 실려 온 반 코드를 앱 어디서든 받는다.**
 *
 * 칠판 안내문의 QR 은 `/?class=1&cls=별빛-3F7K` 로 온다. 예전엔 `?cls=` 를 입장 화면과
 * 코드 복원 화면에서만 읽었는데, **QR 은 첫 화면으로 들어온다** — 그래서 반 코드가
 * 그냥 버려졌고, 아이 그림책은 이 기기에만 저장돼 선생님 화면에 영영 안 떴다.
 * 아무 에러도 안 난다(불변식 3 덕에 화면은 멀쩡히 돈다).
 *
 * 🔴 반만 기억하고 **수업 모드는 안 켠다** — 그건 `?class` 가 따로 한다(classStage.ts).
 *
 * ## 🔴 부모 화면(`/mybook`)에서는 **기억하지 않는다**
 * 그 주소도 `?cls=` 를 달고 있지만 **부모 폰에서 열린다.** 여기서 반을 기억하면
 * 부모 탭이 그 반에 들어간 상태가 되고, 그 폰으로 반 전시장이 열린다 —
 * 부모 화면은 **읽기만** 한다는 경계가 그 자리에서 무너진다.
 * (2026-08-27 수업 한 판 통합 점검이 잡았다. 단위 검사는 화면 하나만 그려서 못 봤다.)
 */
function useClassCodeFromUrl(): void {
  const { search, pathname } = useLocation();
  useEffect(() => {
    if (pathname === ROUTES.familyBook) return;
    const cls = classCodeFromSearch(search);
    if (cls) setClassCode(cls);
  }, [search, pathname]);
}

/** ⏱ 측정 조각은 `?perf=1` 일 때만 받는다 — 아이 화면 첫 코드에 한 줄도 안 더한다(조각 자신은 측정에서 뺀다) */
const PerfReadout = lazy(() => import("./ui/PerfReadout").then((m) => ({ default: m.PerfReadout })));

export function App() {
  useClassCodeFromUrl();
  // 운영자가 고른 색 팔레트를 한 번만 적용한다(:root CSS 변수).
  // 못 받아도 base.css 의 기본 색이 이미 깔려 있어 화면은 정상이다(불변식 3).
  useEffect(() => {
    void applyPalette();
  }, []);

  return (
    <>
      {/* 키보드 사용자 본문 바로가기(접근성) — 포커스 시에만 보임 */}
      <a className="skip-link" href="#main">
        본문 바로가기
      </a>
      {/* 연결 끊김 전역 안내 — 화면마다 제각각이던 실패 처리를 한 곳에서(정직한 안내) */}
      <OfflineBanner />
      {/* 📚 수업 중이면 그렇다고 말한다 — 조용히 숨기면 나중에 쓰는 사람이 「고장」으로 겪는다 */}
      <ClassModeBadge />
      {/* ⏰ 쉬어 가기 · 🌙 밤에는 차분하게 — 보호자가 고른 것만(기본 끔 · lib/rest). 이 기기에서만 센다 */}
      <RestGuard />
      <NightShade />
      {/* ⏱ `?perf=1` 일 때만 — 폰 실기기 첫 구동 측정(조치계획서 ■2). 코드는 이때만 받는다(perf-readout 조각 · 모양 CSS 0.5KB 만 늘 실린다) */}
      {PERF_ON ? (
        <Suspense fallback={null}>
          <PerfReadout />
        </Suspense>
      ) : null}
      <Suspense fallback={<RouteFallback />}>
        <div id="main" tabIndex={-1}>
          {/* 🧩 화면마다 아이콘 묶음 한 장(UiAtlas) — 단추 아이콘이 아홉이어도 요청은 하나(조치계획서 ■2). 묶음 목록은 shared UI_ATLAS */}
          <Routes>
            {/* 랜딩(W-01)은 첫 진입이라 eager 로드 */}
            <Route path={ROUTES.landing} element={<UiAtlas bundle="home"><Landing /></UiAtlas>} />
            <Route path={ROUTES.book} element={<UiAtlas bundle="book"><Book /></UiAtlas>} />
            <Route path={ROUTES.ar} element={<UiAtlas bundle="ar"><Ar /></UiAtlas>} />
            <Route path={ROUTES.verse} element={<UiAtlas bundle="verse"><Verse /></UiAtlas>} />
            <Route path={ROUTES.world} element={<UiAtlas bundle="world"><World /></UiAtlas>} />
            {/* 익명 히어로 코드 복원(웹 동일 출처, FR-HERO-11/16) */}
            <Route path={ROUTES.hero} element={<UiAtlas bundle="info"><HeroRestore /></UiAtlas>} />
            <Route path={`${ROUTES.hero}/:code`} element={<UiAtlas bundle="info"><HeroRestore /></UiAtlas>} />
            {/* 경주 스탬프 랠리(위치기반, FR-RALLY) */}
            <Route path={ROUTES.rally} element={<UiAtlas bundle="info"><Rally /></UiAtlas>} />
            <Route path={ROUTES.community} element={<UiAtlas bundle="info"><Community /></UiAtlas>} />
            <Route path={ROUTES.guide} element={<UiAtlas bundle="info"><Guide /></UiAtlas>} />
            <Route path={ROUTES.family} element={<UiAtlas bundle="family"><Family /></UiAtlas>} />
            <Route path={ROUTES.familyVoice} element={<UiAtlas bundle="family"><FamilyVoice /></UiAtlas>} />
            <Route path={ROUTES.privacy} element={<UiAtlas bundle="info"><Privacy /></UiAtlas>} />
            <Route path={ROUTES.licenses} element={<UiAtlas bundle="info"><Licenses /></UiAtlas>} />
            {/* 👪 부모가 QR 로 여는 그림책 한 권 — **읽기만** 한다(아무것도 안 되살린다).
                검색에 안 걸리게 화면 안에서 noindex 를 붙인다. */}
            <Route path={ROUTES.familyBook} element={<UiAtlas bundle="info"><FamilyBook /></UiAtlas>} />
            {/* 굿즈 상세 공유 링크(QR·SNS, docs/build/16) */}
            <Route path={ROUTES.goods} element={<UiAtlas bundle="info"><GoodsList /></UiAtlas>} />
            <Route path={`${ROUTES.goods}/:id`} element={<UiAtlas bundle="info"><Goods /></UiAtlas>} />
            {/* 관리자 미리보기(iframe) — 허용 출처의 postMessage 로만 내용이 채워진다 */}
            <Route path={ROUTES.goodsPreview} element={<GoodsPreview />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </Suspense>
      {/* 외부 이동 고지(FR-COM-04) — 전역 1회 */}
      <ExternalLinkDialog />
    </>
  );
}
